/**
 * The conversation as bubbles: the one renderer for a call's live transcript
 * and a chat's thread.
 *
 * Owns the bubbles, the faded look of a message still waiting for its session,
 * the typing indicator, the live region and the auto-scroll. Pure
 * presentation: the host feeds it entries and whether a reply is pending.
 *
 * Transcript is ON by default (decision D10) but the parent can hide this
 * element entirely via WebWidgetSettings.realtimeTranscript. Virtualization
 * not needed at MVP scale (typical conversations < 50 messages).
 */

import { LitElement, css, html, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";

import type { TranscriptEntry } from "../state/chat-thread.js";

@customElement("deepagent-transcript")
export class DeepAgentTranscript extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      padding: 16px;
      color: var(--da-text);
      font-family: inherit;
      font-size: 14px;
      line-height: 1.5;
      scroll-behavior: smooth;
      overscroll-behavior: contain;
      scrollbar-color: var(--da-border) transparent;
      scrollbar-width: thin;
      /* Lines fade as they scroll under the header instead of meeting a rule. */
      mask-image: linear-gradient(to bottom, transparent, #000 16px);
    }

    .thread {
      flex: 1 0 auto;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .hint {
      margin: auto;
      text-align: center;
      color: var(--da-text-muted);
      font-size: 13px;
    }

    .turn {
      display: flex;
      max-width: 86%;
      animation: da-bubble-in 180ms cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .turn[data-role="user"] {
      align-self: flex-end;
    }

    .turn[data-role="ai"] {
      align-self: flex-start;
    }

    .bubble {
      min-width: 0;
      padding: 9px 12px;
      border-radius: var(--da-radius-lg, 2px);
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      font-size: 14px;
      line-height: 1.45;
    }

    .turn[data-role="user"] .bubble {
      background: var(--da-accent);
      color: var(--da-accent-fg);
    }

    .turn[data-role="ai"] .bubble {
      background: var(--da-surface);
      color: var(--da-text);
    }

    /* On its way out: shown at once, settled the moment it is really sent. */
    .turn[data-pending] .bubble {
      opacity: 0.55;
    }

    /* The header's face thinks while the agent writes; this line only names it. */
    .typing {
      align-self: flex-start;
      color: var(--da-text-muted);
      font-size: 13px;
      padding: 4px 0;
    }

    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
    }

    @keyframes da-bubble-in {
      from { transform: translateY(6px); opacity: 0; }
      to   { transform: translateY(0);   opacity: 1; }
    }

    @media (prefers-reduced-motion: reduce) {
      .turn { animation: none; }
    }
  `;

  @property({ type: Array }) entries: TranscriptEntry[] = [];
  /** True between a typed message and the agent's written reply. */
  @property({ type: Boolean }) awaitingReply = false;
  @property({ type: String }) labelUser = "You";
  @property({ type: String }) labelAi = "Agent";
  @property({ type: String }) typingLabel = "";
  @property({ type: String }) hint = "Your conversation will appear here.";

  protected override updated(changed: PropertyValues<this>): void {
    if (changed.has("entries") || changed.has("awaitingReply")) {
      // Auto-scroll on new messages (rAF defers until layout settles).
      requestAnimationFrame(() => {
        this.scrollTop = this.scrollHeight;
      });
    }
  }

  override render() {
    return html`
      <div class="thread" role="log" aria-live="polite" aria-relevant="additions">
        ${this.entries.length === 0 && !this.awaitingReply
          ? html`<p class="hint">${this.hint}</p>`
          : null}
        ${repeat(
          this.entries,
          (entry) => entry.id,
          (entry) => this.renderEntry(entry),
        )}
        ${this.awaitingReply
          ? html`<div class="typing">${this.typingLabel}</div>`
          : null}
      </div>
    `;
  }

  private renderEntry(entry: TranscriptEntry) {
    const label = entry.role === "user" ? this.labelUser : this.labelAi;
    // The bubble keeps whitespace as written (pre-wrap), so the template must
    // not put any of its own inside it: a line break here is a line break on
    // screen.
    return html`
      <div class="turn" data-role=${entry.role} ?data-pending=${entry.pending === true}>
        <div class="bubble"><span class="sr-only">${label}: </span>${entry.text}</div>
      </div>
    `;
  }

}

declare global {
  interface HTMLElementTagNameMap {
    "deepagent-transcript": DeepAgentTranscript;
  }
}
